'use client';
/* eslint-disable tailwindcss/no-custom-classname */
import { cn } from '@ui/lib';
import { HTMLProps, useState } from 'react';
import {
  classMapping,
  IconProps,
  Pictogram,
  PictogramProps,
  sizeMapping,
} from '../../elements';
import { StyledText, TextProps } from '../typography';

export type BgColorVariant = 'primary' | 'secondary' | 'tertiary';

export type AvatarPicOptions = {
  pic: Pick<HTMLProps<'img'>, 'src'>;
  fallbackPic?: Pick<HTMLProps<'img'>, 'src'>;
  // For fallback to single initial avatar
  character: string;
};

export type AvatarSingleInitialOption = {
  character: string;
  inverse?: boolean;
};

interface AvatarLoadingOptions {
  loading: true;
}

export interface AvatarIconOptions {
  icon: Pick<IconProps, 'fileName'> | true;
}

export interface CommonAvatarProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  type?: PictogramProps['type'];
  color?: Pick<IconProps, 'color'>;
  className?: string;
  styledText?: Pick<TextProps, 'variant'>;
}

export type AvatarProps = CommonAvatarProps &
  (
    | AvatarPicOptions
    | AvatarSingleInitialOption
    | AvatarLoadingOptions
    | AvatarIconOptions
  );

/**
 * Avatar component that displays a user avatar in one of several forms, based on the provided props:
 *
 * - If a `pic` (image source) is provided, it attempts to display the image.
 *   - If the image fails to load and a `fallbackPic` is provided, it tries the fallback image.
 *   - If both images fail or are missing, and a `character` is provided, it falls back to displaying the character as a single initial avatar.
 * - If an `icon` is provided (and no `pic`), it displays the specified icon as the avatar.
 * - If a `character` is provided (and no `pic` or `icon`), it displays the character as a single initial avatar.
 * - If `loading` is true, it displays a loading spinner avatar.
 *
 * Throws an error if none of the above props are provided.
 *
 * @param props - AvatarProps (see type for details)
 * @returns Avatar component that displays an image, fallback image, character, icon, or loading spinner, depending on the props.
 */
export const PicAvatar = ({
  size = 'md',
  pic,
  fallbackPic,
  className,
  character,
  type,
  color,
  styledText,
}: AvatarPicOptions & AvatarSingleInitialOption & CommonAvatarProps) => {
  const [hasError, setHasError] = useState(false);
  const [picSrc, setPicSrc] = useState(pic.src || fallbackPic?.src);

  // If character is available, fallback to initial avatar when picSrc is empty or hasError is true
  if (character && (hasError || !picSrc)) {
    return (
      <SingleInitialAvatar
        size={size}
        character={character}
        type={type}
        color={color}
        className={className}
        styledText={styledText}
      />
    );
  }

  return (
    picSrc && (
      <img
        alt="Avatar"
        width={sizeMapping[size][1]}
        height={sizeMapping[size][1]}
        className={cn(
          'rounded-full object-cover object-center',
          {
            [classMapping[size]]: size,
          },
          className,
        )}
        src={picSrc}
        // If picSrc is empty, fallback to fallbackPic
        onError={() => {
          if (fallbackPic?.src === picSrc || !fallbackPic?.src) {
            setHasError(true);
            return;
          }
          setPicSrc(fallbackPic?.src ?? '');
        }}
      />
    )
  );
};

export const LoadingAvatar = ({
  size = 'md',
  type,
  color,
  className,
}: AvatarLoadingOptions & CommonAvatarProps) => {
  return (
    <Pictogram
      className={cn('animate-spin-slow', className)}
      icon={{ fileName: 'ic_loading' }}
      size={size}
      type={(type ?? 'brand') as any}
      color={(color ?? 'teal') as any}
      inverse={false}
    />
  );
};

const SizeTypographyMapping = {
  xs: 'title-5',
  sm: 'title-4',
  md: 'title-2',
  lg: 'display-4',
  xl: 'display-2',
};

export const SingleInitialAvatar = ({
  size = 'md',
  character,
  type,
  color,
  className,
  styledText,
  inverse = false,
}: AvatarSingleInitialOption & CommonAvatarProps) => {
  const variant = styledText?.variant ?? SizeTypographyMapping[size];
  return (
    <Pictogram
      size={size}
      className={className}
      type={(type ?? 'brand') as any}
      color={(color ?? 'teal') as any}
      inverse={inverse}
    >
      {(colorClass) => (
        <StyledText variant={variant} className={colorClass}>
          {character}
        </StyledText>
      )}
    </Pictogram>
  );
};

export const IconAvatar = ({
  size = 'md',
  type,
  color,
  icon,
  className,
}: AvatarIconOptions & CommonAvatarProps) => {
  return (
    <Pictogram
      className={className}
      icon={
        icon === true ? { fileName: 'ic_profile_circle' } : (icon as IconProps)
      }
      size={size}
      type={(type ?? 'brand') as any}
      color={(color ?? 'teal') as any}
      inverse={false}
    />
  );
};

export const Avatar = (props: AvatarProps) => {
  // FIXME: May want to update this logic here entirely so we don't have multiple different avatar components
  // Ensure there is a valid pic or fallback source
  const hasValidPic =
    'pic' in props &&
    ((props.pic?.src && props.pic?.src !== '') ||
      (props.fallbackPic?.src && props.fallbackPic?.src !== ''));

  if (hasValidPic) {
    return <PicAvatar {...props} character={props.character} />;
  }

  if ('icon' in props && props.icon) {
    return <IconAvatar {...props} />;
  }

  if ('character' in props && props.character) {
    return <SingleInitialAvatar {...props} />;
  }

  if ('loading' in props && props.loading) {
    return <LoadingAvatar {...props} />;
  }

  // If no valid pic, icon, or character, fallback to generic icon
  if (
    !hasValidPic &&
    !('icon' in props && props.icon) &&
    !('character' in props && props.character)
  ) {
    return <IconAvatar icon />;
  }

  throw new Error('Invalid avatar props');
};
